import { Deg2Rad, Rad2Deg } from './math-utils';

export interface ICoord2D { x: number; y: number }

export interface ISizeRect { width: number; height: number }
export interface ISizeUnknownShape { width?: number; height?: number; radius?: number }

export type IRect = ICoord2D & ISizeRect;
export type ICircle = ICoord2D & { radius: number };

export interface IRectBounds {
  minX: number;
  minY: number;
  maxX: number;
  maxY: number;
}

export class Point {
  public x: number;
  public y: number;

  public static CreateVectorToPoint(startPos: ICoord2D, targetPoint: ICoord2D): Vector {
    return new Vector(targetPoint.x - startPos.x, targetPoint.y - startPos.y);
  }

  public static CreateVectorToCoords(startPos: ICoord2D, targetPos: ICoord2D): Vector {
    return new Vector(targetPos.x - startPos.x, targetPos.y - startPos.y);
  }

  public constructor(x?: number, y?: number) {
    this.x = x || 0;
    this.y = y || 0;
  }

  public Set(x: number, y: number) {
    this.x = x;
    this.y = y;
  }

  public Zero() {
    this.x = 0;
    this.y = 0;
  }

  public VectorToPoint(targetPoint: ICoord2D): Vector {
    return new Vector(targetPoint.x - this.x, targetPoint.y - this.y);
  }

  public VectorToXY(targetX: number, targetY: number): Vector {
    return new Vector(targetX - this.x, targetY - this.y);
  }

  public Add(point: Point) {
    this.x += point.x;
    this.y += point.y;
  }

  public AddValues(x: number, y: number) {
    this.x += x;
    this.y += y;
  }

  public AddCoords(coords: ICoord2D) {
    this.x += coords.x;
    this.y += coords.y;
  }

  public Subtract(point: Point) {
    this.x -= point.x;
    this.y -= point.y;
  }

  public SubtractCoords(coords: ICoord2D) {
    this.x -= coords.x;
    this.y -= coords.y;
  }

  public Equals(p: Point) {
    return this.x === p.x && this.y === p.y;
  }

  public get Coords(): ICoord2D {
    return { x: this.x, y: this.y };
  }

  public Clone(): Point {
    return new Point(this.x, this.y);
  }

  public CloneAdded(vector: Vector): Point {
    return new Point(this.x + vector.x, this.y + vector.y);
  }

  public CloneSubtracted(vector: Vector): Point {
    return new Point(this.x - vector.x, this.y - vector.y);
  }
}

export class Vector extends Point {
  public Normalize() {
    // TODO: Either way better/more efficient?

    // const mag = this.GetMag();
    // if(mag === 0) {
    // }

    // this.x /= mag;
    // this.y /= mag;

    const rad = this.GetRadianAngle();
    this.x = Math.cos(rad);
    this.y = Math.sin(rad);
  }

  public Rotate(degrees: number) {
    const rad = Deg2Rad(degrees);

    const cos = Math.cos(rad);
    const sin = Math.sin(rad);

    const newX = (this.x * cos) - (this.y * sin);
    const newY = (this.x * sin) + (this.y * cos);

    this.x = newX;
    this.y = newY;
  }

  public Scale(scalar: number) {
    this.x *= scalar;
    this.y *= scalar;
  }

  public Clone(): Vector {
    return new Vector(this.x, this.y);
  }

  public ClonePoint(): Point {
    return new Point(this.x, this.y);
  }

  public CloneNormalized(): Vector {
    // TODO: Either way better/more efficient?

    // const mag = this.GetMag();
    // if(mag === 0) {
    // }

    // return new Vector(this.x / mag, this.y / mag);

    const rad = this.GetRadianAngle();

    return new Vector(Math.cos(rad), Math.sin(rad));
  }

  public CloneRotated(degrees: number): Vector {
    const rad = Deg2Rad(degrees);

    const cos = Math.cos(rad);
    const sin = Math.sin(rad);

    const newX = (this.x * cos) - (this.y * sin);
    const newY = (this.x * sin) + (this.y * cos);

    return new Vector(newX, newY);
  }

  public CloneScaled(scalar: number): Vector {
    return new Vector(this.x * scalar, this.y * scalar);
  }

  public CloneAdded(vector: Vector): Vector {
    return new Vector(this.x + vector.x, this.y + vector.y);
  }

  public CloneSubtracted(vector: Vector): Vector {
    return new Vector(this.x - vector.x, this.y - vector.y);
  }

  public GetSqrMag(): number {
    return (this.x * this.x) + (this.y * this.y);
  }

  public GetMag(): number {
    return Math.sqrt(this.GetSqrMag());
  }

  public GetRadianAngle(): number {
    return Math.atan2(this.y, this.x);
  }

  // TODO: Push general rad to deg conversion into math-utils.
  public GetDegreeAngle(): number {
    const rad = this.GetRadianAngle();

    return Rad2Deg(rad);
  }

  public SetRotationRad(radians: number) {
    this.x = Math.cos(radians);
    this.y = Math.sin(radians);
  }

  public SetRotationDeg(degrees: number) {
    this.SetRotationRad(Deg2Rad(degrees));
  }
}

export class GeomUtils {
  /**
   *
   * @param a
   * @param b
   * @returns new ICoords2D interface-object
   */
  public static AddCoords(a: ICoord2D, b: ICoord2D): ICoord2D {
    return { x: a.x + b.x, y: a.y + b.y };
  }

  public static GetVectorAsCoords(start: ICoord2D, target: ICoord2D): ICoord2D {
    return {
      x: target.x - start.x,
      y: target.y - start.y
    };
  }

  public static Rotate(point: ICoord2D, angleRad: number): ICoord2D {
    const cos = Math.cos(angleRad);
    const sin = Math.sin(angleRad);

    return {
      x: (point.x * cos) - (point.y * sin),
      y: (point.x * sin) + (point.y * cos)
    };
  }

  // Gets the distance between 2 points, squared (most of the time you dont need the square root if just comparing distances)
  public static GetDistSqr(a: ICoord2D, b: ICoord2D): number {
    const diffVector = {
      x: a.x - b.x,
      y: a.y - b.y
    };
    return this.GetSqrMag(diffVector);
  }

  public static GetSqrMag(coordsAsVector: ICoord2D): number {
    return (coordsAsVector.x * coordsAsVector.x) + (coordsAsVector.y * coordsAsVector.y);
  }

  public static GetMag(coordsAsVector: ICoord2D): number {
    return Math.sqrt(this.GetSqrMag(coordsAsVector));
  }

  public static GetRadianAngle(coordsAsVector: ICoord2D): number {
    return Math.atan2(coordsAsVector.y, coordsAsVector.x);
  }

  /**
   * Will mutate the given coords object.
   * @param coordsAsVector
   */
  public static normalize(coordsAsVector: ICoord2D) {
    const rad = this.GetRadianAngle(coordsAsVector);
    coordsAsVector.x = Math.cos(rad);
    coordsAsVector.y = Math.sin(rad);
  }

  public static getNormalized(coordsAsVector: ICoord2D): ICoord2D {
    const rad = this.GetRadianAngle(coordsAsVector);
    return { x: Math.cos(rad), y: Math.sin(rad) };
  }

  public static cloneScaled(coordsAsVector: ICoord2D, scalar: number): ICoord2D {
    return { x: coordsAsVector.x * scalar, y: coordsAsVector.y * scalar };
  }

  /**
   * This function will return a scale value required to change an already scaled figure to the next given scale value as if it was currently scaled by 1.
   * E.g. figure x is currently scaled to x3 the original size, and you want it to be x10 the ORIGINAL size (not it's current size),
   * this will return 3.33~, the factor to scale to CURRENT size by to achieve the desired result.
   * The was specifically written because matter.js scales off the current body size, not the original.
   * (After scaling once, you cannot return to the original size by scaling again by x1, it'll do nothing.)
   * @param currenScale The current scale value relative to 1
   * @param nextRelativeScale The next scale value desired to achieve, relative to 1
   * @returns The value to scale the currently scaled figure by.
   */
  public static GetNextAbsoluteScaleFactor(currenScale: number, nextRelativeScale: number) {
    return (1 / currenScale) * nextRelativeScale;
  }

  // This calculates if the queryPoint is anywhere in the 2D plane perfectly perpendicular to the line segment start --> end.
  public static CalcIsInsideLineSegment(startPoint: ICoord2D, endPoint: ICoord2D, queryPoint: ICoord2D) {
    const startToEndX = endPoint.x - startPoint.x;
    const startToEndY = endPoint.y - startPoint.y;

    const L2 = (startToEndX * startToEndX) + (startToEndY * startToEndY);
    if (L2 === 0) { return false; }

    const r = (((queryPoint.x - startPoint.x) * startToEndX) + ((queryPoint.y - startPoint.y) * startToEndY)) / L2;

    return (r >= 0) && (r <= 1);
  }

  /**
   *
   * @param startPoint
   * @param endPoint
   * @param queryPoint
   * @returns true if the queryPoint is within the radius of the line defined by the start and end points.
   */
  public static CalcIsInsideRadius(startPoint: ICoord2D, endPoint: ICoord2D, queryPoint: ICoord2D) {
    const radToEnd = (new Vector(endPoint.x - startPoint.x, endPoint.y - startPoint.y)).GetSqrMag();
    const radToPoint = (new Vector(queryPoint.x - startPoint.x, queryPoint.y - startPoint.y)).GetSqrMag();

    return radToPoint <= radToEnd;
  }

  public static CheckContactCircleWithRect(circle: ICircle, rect: IRect) {
    const halfWidth = rect.width / 2;
    const halfHeight = rect.height / 2;

    const cx = Math.abs(circle.x - rect.x - halfWidth);
    const cy = Math.abs(circle.y - rect.y - halfHeight);
    const xDist = halfWidth + circle.radius;
    const yDist = halfHeight + circle.radius;

    if (cx > xDist || cy > yDist) {
      return false;
    }

    if (cx <= halfWidth || cy <= halfHeight) {
      return true;
    }

    const xCornerDist = cx - halfWidth;
    const yCornerDist = cy - halfHeight;
    const xCornerDistSq = xCornerDist * xCornerDist;
    const yCornerDistSq = yCornerDist * yCornerDist;
    const maxCornerDistSq = circle.radius * circle.radius;

    return (xCornerDistSq + yCornerDistSq <= maxCornerDistSq);
  }

  /**
   * Returns the angle of the line between two points against x-axis
   * @param p1 The first point
   * @param p2 The second point
   * @returns The angle is in radians
   */
  public static GetAngleBetweenPoints(p1: ICoord2D, p2: ICoord2D): number {
    const dx = p2.x - p1.x;
    const dy = p2.y - p1.y;

    return Math.atan2(dy, dx);
  }

  public static GetRandomPointOnCircle(center: ICoord2D, radius: number): ICoord2D {
    const angle = Math.random() * Math.PI * 2;

    const x = center.x + radius * Math.cos(angle);
    const y = center.y + radius * Math.sin(angle);

    return { x, y };
  }

  public static GetRandomPointInCircleArea(center: ICoord2D, radius: number): ICoord2D {
    // if simply use Math.random() to generate a radius value, you would end up with a higher density of points near the center of the circle,
    // rather than a uniform distribution across the entire area.
    const randomRadius = Math.sqrt(Math.random()) * radius;

    return this.GetRandomPointOnCircle(center, randomRadius);
  }

  /**
   * Point-in-polygon (ray casting). Polygon is treated as implicitly closed.
   */
  public static PointInPolygon(x: number, y: number, polygon: ICoord2D[]): boolean {
    let inside = false;
    for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
      const xi = polygon[i].x;
      const yi = polygon[i].y;
      const xj = polygon[j].x;
      const yj = polygon[j].y;
      if ((yi > y) !== (yj > y) && x < ((xj - xi) * (y - yi)) / (yj - yi + 1e-12) + xi) { inside = !inside; }
    }
    return inside;
  }

  /**
   * Rejection-samples inside the polygon's axis-aligned bounding box until a point lies inside
   * (`PointInPolygon`), or returns the **average of vertices** (not a geometric centroid).
   *
   * **Distribution:** not uniform over the interior — narrow regions get fewer hits than a uniform sampler would.
   * Acceptable for casual spawn jitter (e.g. filling base-match spawns inside nav); prefer dedicated polygon
   * samplers if uniformity matters.
   */
  public static GetRandomPointInSimplePolygon(polygon: ICoord2D[], maxAttempts = 400): ICoord2D {
    if (polygon.length < 3) { return { x: 0, y: 0 }; }
    let minX = polygon[0].x;
    let maxX = polygon[0].x;
    let minY = polygon[0].y;
    let maxY = polygon[0].y;
    for (const p of polygon) {
      minX = Math.min(minX, p.x);
      maxX = Math.max(maxX, p.x);
      minY = Math.min(minY, p.y);
      maxY = Math.max(maxY, p.y);
    }
    for (let k = 0; k < maxAttempts; k++) {
      const x = minX + Math.random() * (maxX - minX);
      const y = minY + Math.random() * (maxY - minY);
      if (this.PointInPolygon(x, y, polygon)) { return { x, y }; }
    }
    const cx = polygon.reduce((s, p) => s + p.x, 0) / polygon.length;
    const cy = polygon.reduce((s, p) => s + p.y, 0) / polygon.length;
    return { x: cx, y: cy };
  }

  /**
   * World-space corners of a Tiled **rectangle** object: `origin` is the **top-left** of the unrotated rect
   * (same convention as `Matter.Bodies.rectangle` + `Body.setCentre` to that corner, then `Body.setAngle`).
   * Rotation is in **degrees** (Tiled); pivot is `origin`. Winding matches Matter's `Vertices.fromPath` rect: TL → TR → BR → BL.
   */
  public static GetTiledRectangleWorldVertices(origin: ICoord2D, width: number, height: number, rotationDeg: number): ICoord2D[] {
    const { x: px, y: py } = origin;
    const corners: ICoord2D[] = [
      { x: px, y: py },
      { x: px + width, y: py },
      { x: px + width, y: py + height },
      { x: px, y: py + height }
    ];
    const rad = Deg2Rad(rotationDeg);
    if (rad === 0) { return corners; }
    return corners.map((c) => this.AddCoords(origin, this.Rotate(this.GetVectorAsCoords(origin, c), rad)));
  }

  public static CalculateSimpleCenter(vertices: ICoord2D[]): ICoord2D {
    let minX = vertices[0].x;
    let maxX = vertices[0].x;
    let minY = vertices[0].y;
    let maxY = vertices[0].y;

    for (const vertex of vertices) {
      minX = Math.min(minX, vertex.x);
      maxX = Math.max(maxX, vertex.x);
      minY = Math.min(minY, vertex.y);
      maxY = Math.max(maxY, vertex.y);
    }

    return {
      x: (minX + maxX) / 2,
      y: (minY + maxY) / 2
    };
  }

  /**
   * Simply returns a vertex on the opposite x-side of another
   * @param baseVert A non-moving vertex uses as the basis on which the other will be relatively moved
   * @param flippingVert The moving vertex
   */
  public static flipVertex(baseVert: ICoord2D, flippingVert: ICoord2D, flipX: boolean, flipY: boolean): ICoord2D {
    const diffX = baseVert.x - flippingVert.x;
    const diffY = baseVert.y - flippingVert.y;
    return {
      x: flipX ? baseVert.x + diffX : flippingVert.x,
      y: flipY ? baseVert.y + diffY : flippingVert.y
    };
  }
}
